/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, CheckCircle2, ExternalLink, Code2, Truck } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";

const SITE = "https://equisight.nl";

const SNIPPET_BASIC = `<!-- EquiSight Vervoersdocument-generator widget -->
<iframe
  src="${SITE}/embed/transport-doc"
  title="EquiSight vervoersdocument paard"
  width="100%"
  height="780"
  style="border:0;border-radius:16px;max-width:680px"
  loading="lazy"
  allow="clipboard-write"></iframe>
<p style="font-size:12px;color:#666;text-align:center;margin-top:8px">
  Gratis tool aangeboden door
  <a href="${SITE}/tools/vervoersdocument-paard" target="_blank" rel="noopener">EquiSight</a>
</p>`;

const SNIPPET_RESPONSIVE = `<!-- EquiSight responsive embed -->
<div style="position:relative;padding-bottom:120%;height:0;max-width:680px;margin:0 auto">
  <iframe
    src="${SITE}/embed/transport-doc"
    title="EquiSight vervoersdocument paard"
    style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;border-radius:16px"
    loading="lazy"
    allow="clipboard-write"></iframe>
</div>`;

const CopyBlock = ({ code, testid }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  return (
    <div className="relative">
      <pre
        data-testid={testid}
        className="overflow-x-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-4 text-xs leading-relaxed text-[hsl(var(--ink))]"
      >
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[11px] font-medium text-[hsl(var(--ink))] shadow-sm hover:bg-[hsl(var(--surface))]"
      >
        {copied ? (
          <><CheckCircle2 className="h-3 w-3" /> Gekopieerd</>
        ) : (
          <><Copy className="h-3 w-3" /> Kopieer</>
        )}
      </button>
    </div>
  );
};

const WIDGETS = [
  {
    id: "transport-doc",
    icon: Truck,
    title: "Vervoersdocument-generator",
    description:
      "Bezoekers van jouw site genereren in 60 seconden een NVWA-conform vervoersdocument (NL en EU). Inclusief 3-staps wizard, automatische verplichte-documenten engine en PDF-download. Wij hosten, jij ontvangt de tool.",
    embedPath: "/embed/transport-doc",
    audience: "Maneges, paardenartsen, transporteurs, hoefsmeden, fokkerij-blogs",
    status: "live",
  },
];

export default function WidgetsPage() {
  return (
    <main data-testid="page-widgets" className="bg-background">
      <Seo
        title="Gratis paardentools embedden | EquiSight Widgets"
        description="Embed gratis EquiSight-paardentools (vervoersdocument-generator, agenda, fokkerij-rekenmachines) op je eigen website. Eén regel code, geen account, geen kosten."
        path="/widgets"
        keywords={["paardentool embed", "vervoersdocument widget", "equisight embed"]}
        breadcrumbs={[{ name: "Widgets", path: "/widgets" }]}
      />
      <AnnouncementBar />
      <Header />
      <Breadcrumbs items={[{ name: "Widgets", path: "/widgets" }]} />

      <section className="mx-auto max-w-4xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          Voor partners, blogs &amp; maneges
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
          Voeg een EquiSight-tool toe aan je site.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
          Gratis, geen account, geen tracking pixels. Geef je bezoekers
          praktische paardentools en kies zelf hoe je ze inbedt.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {[
            { label: "Hosting", value: "Wij hosten" },
            { label: "Updates", value: "Automatisch" },
            { label: "Kosten", value: "€0 — voor altijd" },
          ].map((b) => (
            <div
              key={b.label}
              className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">{b.label}</p>
              <p className="mt-1 font-display text-xl text-[hsl(var(--ink))]">{b.value}</p>
            </div>
          ))}
        </div>

        {WIDGETS.map((w) => {
          const Icon = w.icon;
          return (
            <article
              key={w.id}
              data-testid={`widget-${w.id}`}
              className="mt-12 rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 lg:p-10"
            >
              <div className="flex items-start gap-4">
                <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-[hsl(var(--forest))]/10 text-[hsl(var(--forest))]">
                  <Icon className="h-6 w-6" />
                </span>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-display text-2xl font-semibold text-[hsl(var(--ink))]">{w.title}</h2>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      {w.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--ink-soft))]">{w.description}</p>
                  <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
                    Geschikt voor: <span className="normal-case text-[hsl(var(--ink))]">{w.audience}</span>
                  </p>
                  <Link
                    to={w.embedPath}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`widget-preview-${w.id}`}
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--forest))] underline"
                  >
                    Bekijk live preview <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>

              <div className="mt-8 space-y-6">
                <div>
                  <p className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
                    <Code2 className="h-3.5 w-3.5" /> Basis-embed (vaste hoogte)
                  </p>
                  <CopyBlock code={SNIPPET_BASIC} testid={`snippet-${w.id}-basic`} />
                </div>
                <div>
                  <p className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
                    <Code2 className="h-3.5 w-3.5" /> Responsive-embed (schaalt mee)
                  </p>
                  <CopyBlock code={SNIPPET_RESPONSIVE} testid={`snippet-${w.id}-responsive`} />
                </div>
              </div>
            </article>
          );
        })}

        <div className="mt-12 rounded-3xl bg-[hsl(var(--forest))] p-6 text-white lg:p-10">
          <h2 className="font-display text-2xl font-semibold">Een widget op maat?</h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85">
            Manege-agenda, vaccinatiekalender of fokkerij-rekenmachine — laat
            weten welke tool je wilt embedden en wij bouwen 'm met jouw
            huisstijl.
          </p>
          <Link
            to="/contact"
            data-testid="widget-request"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-[hsl(var(--forest))]"
          >
            Stuur een verzoek <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>

        <p className="mt-10 text-xs text-[hsl(var(--ink-soft))]">
          Door deze widget op je site te embedden, geef je aan dat je akkoord
          gaat met de{" "}
          <Link to="/voorwaarden" className="underline">EquiSight gebruiksvoorwaarden</Link>.
          Wij stellen geen tracking-cookies via de iframe.
        </p>
      </section>

      <Footer />
    </main>
  );
}
